'use client';

import { useState, useEffect, useMemo } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import Link from 'next/link';
import { useAuth } from '@/app/context/AuthContext';
import { useVersion } from '@/app/context/VersionContext';
import { useSavedLibrary } from '@/app/hooks/useSavedLibrary';
import { LibraryCard } from '@/app/saved/components/LibraryCard';
import { QuickLookModal } from '@/app/saved/components/QuickLookModal';
import { PublishedDesigns } from '@/app/saved/components/PublishedDesigns';
import { SECONDARY_BUTTON } from '@/app/components/ui/actionButtons';
import {
  TYPE_META, GROUPS, folderNameProblem, tagProblem, prepareOpen, openHref,
  type LibraryType, type LibraryItem,
} from '@/app/saved/lib/library';

type CatFilter = LibraryType | 'all';
type LibraryTab = 'saved' | 'published';
const UNFILED = '__unfiled';
/** Cards rendered before "Show more". Every card was rendered at once: 1,000 builds took ~0.7s on a
 *  desktop (break-ui, 2026-10-24). Counts, export and select still use the WHOLE filtered list. */
const PAGE_SIZE = 120;
/** "1 build", "3 builds" — never "1 build(s)". */
const countLabel = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

export default function SavedPage() {
  const router = useRouter();
  // The tab lives in the URL (?tab=published) so "Your published designs" links, the redirect from the
  // old /designs/mine, and the back button all land on the right one.
  const searchParams = useSearchParams();
  const tab: LibraryTab = searchParams.get('tab') === 'published' ? 'published' : 'saved';
  const selectTab = (next: LibraryTab) => router.replace(next === 'published' ? '/saved?tab=published' : '/saved', { scroll: false });
  const { user, loading: authLoading } = useAuth();
  // The version the user is ACTUALLY working in, not the app default — the header states it and
  // the cards compare against it.
  const { version: workingVersion } = useVersion();
  const {
    items, loading, folders, allTags, remove, duplicate, rename,
    setFolder: moveToFolder, renameFolder, deleteFolder, setTags: setItemTags, setPinned: setItemPinned,
  } = useSavedLibrary();

  const [cat, setCat] = useState<CatFilter>('all');
  const [folder, setFolder] = useState<string | null>(null);
  const [tags, setTags] = useState<Set<string>>(new Set());
  const [q, setQ] = useState('');
  const [ver, setVer] = useState('');
  const [sort, setSort] = useState<'new' | 'old' | 'az' | 'type'>('new');
  const [view, setView] = useState<'grid' | 'list'>('grid');
  // Vanilla/modded toggle for reference bookmarks — only favorites/recipes carry a namespaced id
  // to test (a build's modded-ness lives inside its opaque stateHash), so it shows only there.
  const [source, setSource] = useState<'all' | 'vanilla' | 'modded'>('all');
  const [copiedUid, setCopiedUid] = useState<string | null>(null);
  const [modalUid, setModalUid] = useState<string | null>(null);
  const [draggingItem, setDraggingItem] = useState<LibraryItem | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const showToast = (msg: string) => { setToast(msg); setTimeout(() => setToast(t => (t === msg ? null : t)), 2200); };
  const [selecting, setSelecting] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  // Below lg the folder + type lists sit ABOVE the builds, and open they pushed the first card about two
  // phone screens down. They start closed there; from lg up they are a side rail and always shown.
  const [filtersOpen, setFiltersOpen] = useState(false);

  // The modal reads the LIVE item so optimistic edits (rename/tags/folder/pin) reflect instantly.
  // If the item is deleted while open, this resolves to null and the modal simply stops rendering;
  // a stale modalUid is harmless (it's replaced the next time a card is opened).
  const modalItem = useMemo(() => items.find(it => it.uid === modalUid) ?? null, [items, modalUid]);

  // Changing tab — including through a link, e.g. "Your published designs" inside the quick look — closes
  // the quick look and ends a selection. Both belong to Saved: the quick look used to stay open on top of
  // the Published tab, and would have reopened on the way back. (State adjusted during render, the
  // React-documented way to reset state when a value changes.)
  const [tabShown, setTabShown] = useState<LibraryTab>(tab);
  if (tabShown !== tab) {
    setTabShown(tab);
    setModalUid(null);
    setSelecting(false);
    setSelected(new Set());
  }

  useEffect(() => {
    if (!authLoading && !user) router.replace(`/login?redirect=${encodeURIComponent(tab === 'published' ? '/saved?tab=published' : '/saved')}`);
  }, [authLoading, user, router, tab]);

  // counts per type + list of versions present, for the nav and the version filter
  const { counts, versions } = useMemo(() => {
    const counts: Partial<Record<CatFilter, number>> = {};
    const versionSet = new Set<string>();
    for (const it of items) {
      counts[it.type] = (counts[it.type] ?? 0) + 1;
      if (it.version) versionSet.add(it.version);
    }
    return { counts, versions: [...versionSet].sort((a, b) => b.localeCompare(a, undefined, { numeric: true })) };
  }, [items]);

  const folderCount = useMemo(() => {
    const c: Record<string, number> = { [UNFILED]: 0 };
    for (const it of items) {
      if (it.folder) c[it.folder] = (c[it.folder] ?? 0) + 1;
      else if (it.source === 'element') c[UNFILED]++;
    }
    return c;
  }, [items]);

  const query = q.toLowerCase().trim();
  const showSourceFilter = cat === 'favorites' || cat === 'recipes';
  const isVanillaId = (id: string) => !id.includes(':') || id.startsWith('minecraft:');
  const visible = useMemo(() => {
    const list = items.filter(it => {
      if (cat !== 'all' && it.type !== cat) return false;
      if (folder === UNFILED && it.folder) return false;
      if (folder && folder !== UNFILED && it.folder !== folder) return false;
      if (tags.size && ![...tags].every(t => it.tags.includes(t))) return false;
      if (ver && it.version !== ver) return false;
      // vanilla/modded applies only in the favorites/recipes views (they hold a namespaced item id)
      if (source !== 'all' && (cat === 'favorites' || cat === 'recipes') && it.source === 'favorite') {
        const vanilla = isVanillaId(typeof it.metadata.item_id === 'string' ? it.metadata.item_id : '');
        if (source === 'vanilla' ? !vanilla : vanilla) return false;
      }
      if (query && !it.name.toLowerCase().includes(query) && !it.summary.toLowerCase().includes(query)) return false;
      return true;
    });
    list.sort((a, b) => {
      switch (sort) {
        case 'new': return +new Date(b.createdAt) - +new Date(a.createdAt);
        case 'old': return +new Date(a.createdAt) - +new Date(b.createdAt);
        case 'az': return a.name.localeCompare(b.name);
        case 'type': return TYPE_META[a.type].label.localeCompare(TYPE_META[b.type].label) || (+new Date(b.createdAt) - +new Date(a.createdAt));
      }
    });
    // pinned float to the top in the unfiltered All view, where "keep handy" makes sense
    if (cat === 'all' && !folder) list.sort((a, b) => (b.pinned ? 1 : 0) - (a.pinned ? 1 : 0));
    return list;
  }, [items, cat, folder, tags, ver, query, sort, source]);
  // How many of `visible` are rendered. Keyed to the filters, so changing any of them starts again at
  // one page (derived, not reset in an effect: no extra render with the old count).
  const filterKey = [cat, folder, [...tags].join(','), ver, query, sort, source].join('|');
  const [shown, setShown] = useState({ key: filterKey, n: PAGE_SIZE });
  const shownCount = shown.key === filterKey ? shown.n : PAGE_SIZE;

  // tag cloud, scoped to the active folder/type so it reflects what you're looking at
  const scopedTags = useMemo(() => {
    const c: Record<string, number> = {};
    for (const it of items) {
      if (cat !== 'all' && it.type !== cat) continue;
      if (folder === UNFILED && it.folder) continue;
      if (folder && folder !== UNFILED && it.folder !== folder) continue;
      it.tags.forEach(t => { c[t] = (c[t] ?? 0) + 1; });
    }
    return Object.keys(c).sort((a, b) => c[b] - c[a] || a.localeCompare(b)).map(t => ({ t, n: c[t] }));
  }, [items, cat, folder]);

  // ⚠️ openHref, NOT openPath. `openPath` is the bare tool route; `openHref` carries the build's
  // saved version and mod context. Sharing the bare path handed the recipient a link that opened
  // at THEIR selected version — so a 1.20.4 sign shared to someone on 26.2 produced a modern-era
  // payload for an old server, which is the blank-sign class of defect. Worse, the Share button
  // sits two rows below the modal banner that promises "Made for 1.20.4", so the page contradicted
  // itself. Every other open path already goes through openHref; this one was the exception.
  const copyShare = async (item: LibraryItem) => {
    try {
      await navigator.clipboard.writeText(`${window.location.origin}${openHref(item)}`);
      setCopiedUid(item.uid);
      setTimeout(() => setCopiedUid(c => (c === item.uid ? null : c)), 2000);
    } catch { /* ignore */ }
  };

  const openTool = (item: LibraryItem) => prepareOpen(item);

  // Rename can genuinely fail — the dedupe key travels with the name, and another build of the
  // same type may already hold it. updateElement rolls the card back on failure, so without this
  // the name would simply snap back with no explanation.
  const handleRename = (item: LibraryItem, name: string) => {
    void rename(item, name).then(({ error }) => {
      if (error) showToast(/duplicate|unique/i.test(error) ? `You already have a ${TYPE_META[item.type].label.toLowerCase()} called "${name}".` : 'Could not rename that build.');
    });
  };

  // drag-to-folder: move the dragged element build into `target` (null = Unfiled)
  const dropToFolder = (target: string | null) => {
    const dragged = draggingItem;
    setDraggingItem(null);
    if (!dragged || dragged.source !== 'element') return;
    if (dragged.folder === target) return;
    // Toast the OUTCOME: announcing "Moved" before the write returned contradicted a card that snapped back.
    void moveToFolder([dragged.id], target).then(({ error }) =>
      showToast(error ? `Could not move “${dragged.name}”.` : `Moved “${dragged.name}” to ${target ?? 'Unfiled'}`));
  };
  const dropToNewFolder = () => {
    const dragged = draggingItem;
    setDraggingItem(null);
    if (!dragged || dragged.source !== 'element') return;
    const name = window.prompt('New folder name')?.trim();
    if (!name) return;
    const problem = folderNameProblem(name);
    if (problem) { showToast(problem); return; }
    moveToFolder([dragged.id], name);
    showToast(`Created “${name}” and moved “${dragged.name}” in`);
  };

  const hasElements = useMemo(() => items.some(it => it.source === 'element'), [items]);

  const clearFilters = () => { setCat('all'); setFolder(null); setTags(new Set()); setVer(''); setQ(''); };
  const addTag = (t: string) => setTags(prev => new Set(prev).add(t));

  // ── bulk selection ──
  const toggleSelect = (item: LibraryItem) =>
    setSelected(prev => {
      const n = new Set(prev);
      if (n.has(item.uid)) n.delete(item.uid); else n.add(item.uid);
      return n;
    });
  const exitSelect = () => { setSelecting(false); setSelected(new Set()); };
  const selectedItems = useMemo(() => items.filter(it => selected.has(it.uid)), [items, selected]);
  const selectedElementIds = selectedItems.filter(it => it.source === 'element').map(it => it.id);

  const exportItems = (list: LibraryItem[]) => {
    if (list.length === 0) return;
    const payload = {
      app: 'mcworldtools', kind: 'saved-library-export', exportedAt: new Date().toISOString(), count: list.length,
      items: list.map(it => ({
        type: it.type, source: it.source, name: it.name, version: it.version,
        folder: it.folder, tags: it.tags, openPath: it.openPath, summary: it.summary, metadata: it.metadata,
      })),
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `mcworldtools-saved-${new Date().toISOString().slice(0, 10)}.json`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
    showToast(`Exported ${list.length} item${list.length === 1 ? '' : 's'}`);
  };

  const bulkMove = () => {
    if (selectedElementIds.length === 0) { showToast('Only builds (not favorites) can go in folders'); return; }
    const target = window.prompt(`Move ${countLabel(selectedElementIds.length, 'build', 'builds')} to which folder?\n(existing: ${folders.join(', ') || 'none'} — leave blank for Unfiled)`);
    if (target === null) return;
    const name = target.trim();
    const problem = name === '' ? null : folderNameProblem(name);
    if (problem) { showToast(problem); return; }
    moveToFolder(selectedElementIds, name === '' ? null : name);
    const skipped = selectedItems.length - selectedElementIds.length;
    showToast(`Moved ${countLabel(selectedElementIds.length, 'build', 'builds')} to ${name || 'Unfiled'}${skipped ? ` · ${countLabel(skipped, 'favorite', 'favorites')} skipped` : ''}`);
    exitSelect();
  };
  const bulkTag = () => {
    if (selectedElementIds.length === 0) { showToast('Only builds (not favorites) can be tagged'); return; }
    const raw = window.prompt(`Add a tag to ${countLabel(selectedElementIds.length, 'build', 'builds')}:`);
    if (!raw) return;
    const tag = raw.trim().toLowerCase().replace(/\s+/g, '-');
    if (!tag) return;
    const problem = tagProblem(tag);
    if (problem) { showToast(problem); return; }
    selectedItems.forEach(it => { if (it.source === 'element' && !it.tags.includes(tag)) setItemTags(it, [...it.tags, tag]); });
    const skipped = selectedItems.length - selectedElementIds.length;
    showToast(`Tagged ${countLabel(selectedElementIds.length, 'build', 'builds')} #${tag}${skipped ? ` · ${countLabel(skipped, 'favorite', 'favorites')} skipped` : ''}`);
    exitSelect();
  };
  const bulkDelete = () => {
    if (!window.confirm(`Delete ${countLabel(selectedItems.length, 'item', 'items')}? This cannot be undone.`)) return;
    selectedItems.forEach(remove);
    showToast(`Deleted ${countLabel(selectedItems.length, 'item', 'items')}`);
    exitSelect();
  };
  const removeTag = (t: string) => setTags(prev => { const n = new Set(prev); n.delete(t); return n; });

  if (authLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-6 h-6 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (!user) return null;

  const noFilter = cat === 'all' && !folder && tags.size === 0;
  // What the closed disclosure says is applied, so a filter hidden behind it is never invisible.
  const railFilterLabel = [
    folder ? (folder === UNFILED ? 'Unfiled' : folder) : null,
    cat !== 'all' ? TYPE_META[cat].label : null,
  ].filter(Boolean).join(' · ');
  // A pick below lg closes the disclosure so the result is on screen; at lg+ the rail ignores `filtersOpen`.
  const pickFilter = (apply: () => void) => { apply(); setFiltersOpen(false); };

  return (
    <div className="max-w-[1240px] mx-auto p-4 md:p-6">
      {/* header */}
      <div className="flex items-center gap-3 mb-4">
        <svg className="w-7 h-7 text-emerald-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
        </svg>
        <div>
          <h1 className="text-2xl font-bold text-zinc-100">My Library</h1>
          <p className="text-sm text-zinc-500">Every build, favorite and profile you&apos;ve kept, and the designs you&apos;ve shared — in one place.</p>
        </div>
        {tab === 'saved' && (
          <span className="ml-auto hidden sm:inline-flex shrink-0 whitespace-nowrap items-center gap-1.5 text-[11.5px] text-zinc-500 bg-zinc-900 border border-zinc-800 rounded-full px-2.5 py-1" title="Builds open at their own saved version; this is the app default.">
            Working in <b className="text-zinc-300 font-mono">{workingVersion}</b>
          </span>
        )}
      </div>

      <div role="tablist" aria-label="Library sections" className="flex gap-1 bg-zinc-900 rounded-xl border border-zinc-800 p-1 w-full lg:w-[216px] mb-5">
        {(['saved', 'published'] as const).map(t => (
          <button
            key={t}
            type="button"
            role="tab"
            aria-selected={tab === t}
            onClick={() => selectTab(t)}
            className={`flex-1 inline-flex items-center justify-center gap-2 px-3.5 py-1.5 rounded-lg text-sm transition-colors ${tab === t ? 'bg-zinc-800 text-white' : 'text-zinc-400 hover:text-white'}`}
          >
            {t === 'saved' ? 'Saved' : 'Published'}
            {t === 'saved' && <span className="text-[11px] tabular-nums text-zinc-500">{items.length}</span>}
          </button>
        ))}
      </div>

      {tab === 'published' ? (
        <PublishedDesigns />
      ) : (
      // Rail beside the builds only from lg: at md it left one narrow column of cards.
      <div className="grid grid-cols-1 lg:grid-cols-[216px_1fr] gap-5 items-start">
        {/* ── nav rail ── */}
        <nav className="lg:sticky lg:top-4 flex flex-col gap-3.5">
          {/* ⚠️ "All" stays visible at EVERY width — authenticated-layout, community-layout and the saved specs
              wait on `getByRole('button', { name: /^All\b/ })` as their page-ready signal. */}
          <div className="flex gap-2">
            <button
              onClick={() => pickFilter(() => { setCat('all'); setFolder(null); })}
              className={`flex-1 min-w-0 flex items-center justify-between gap-2 px-3 py-2.5 rounded-xl border text-[13.5px] font-semibold transition-colors ${
                noFilter ? 'border-emerald-500/50 bg-emerald-500/10 text-zinc-100' : 'border-zinc-800 bg-zinc-900 text-zinc-200 hover:border-zinc-700'
              }`}
            >
              <span>All</span>
              <span className="text-[12px] text-emerald-400 tabular-nums">{items.length}</span>
            </button>
            <button
              type="button"
              onClick={() => setFiltersOpen(o => !o)}
              aria-expanded={filtersOpen}
              aria-controls="library-filters"
              className={`lg:hidden min-w-0 max-w-[60%] inline-flex items-center gap-2 px-3 py-2.5 rounded-xl border text-[13px] font-semibold transition-colors ${
                railFilterLabel ? 'border-emerald-500/50 bg-emerald-500/10 text-zinc-100' : 'border-zinc-800 bg-zinc-900 text-zinc-200 hover:border-zinc-700'
              }`}
            >
              <span className="whitespace-nowrap">Folders &amp; types</span>
              {railFilterLabel && <span className="min-w-0 truncate text-[12px] font-normal text-emerald-400">{railFilterLabel}</span>}
              <svg aria-hidden viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className={`w-3.5 h-3.5 shrink-0 transition-transform ${filtersOpen ? 'rotate-180' : ''}`}><path strokeLinecap="round" strokeLinejoin="round" d="M6 9l6 6 6-6" /></svg>
            </button>
          </div>

          <div
            id="library-filters"
            className={`${filtersOpen ? 'grid' : 'hidden'} grid-cols-1 sm:grid-cols-2 items-start gap-3.5 lg:flex lg:flex-col lg:items-stretch`}
          >
          {hasElements && (
            <div className="border border-zinc-800/70 rounded-xl overflow-hidden bg-zinc-900">
              <h3 className="px-3 pt-2 pb-1.5 text-[10.5px] tracking-[0.09em] uppercase text-zinc-500 font-bold">Folders</h3>
              {folders.map(f => (
                <FolderRow
                  key={f} name={f} count={folderCount[f] ?? 0} active={folder === f}
                  dropActive={!!draggingItem}
                  onClick={() => pickFilter(() => setFolder(folder === f ? null : f))}
                  onDrop={() => dropToFolder(f)}
                  onRename={() => {
                    const to = window.prompt('Rename folder', f)?.trim();
                    if (!to || to === f) return;
                    const problem = folderNameProblem(to);
                    if (problem) { showToast(problem); return; }
                    void renameFolder(f, to).then(({ error }) => { if (error) showToast('Some builds could not be moved — try renaming the folder again.'); });
                    if (folder === f) setFolder(to);
                  }}
                  onDelete={() => {
                    if (window.confirm(`Delete folder “${f}”? Its ${countLabel(folderCount[f] ?? 0, 'build moves', 'builds move')} to Unfiled — nothing is deleted.`)) {
                      void deleteFolder(f).then(({ error }) => { if (error) showToast('Some builds could not be moved to Unfiled — try again.'); });
                      if (folder === f) setFolder(null);
                    }
                  }}
                />
              ))}
              {folderCount[UNFILED] > 0 && (
                <NavRow
                  label="Unfiled" italic count={folderCount[UNFILED]} active={folder === UNFILED} folder
                  dropActive={!!draggingItem}
                  onClick={() => pickFilter(() => setFolder(folder === UNFILED ? null : UNFILED))}
                  onItemDrop={() => dropToFolder(null)}
                />
              )}
              <button
                onClick={() => showToast('Drag a build here, or use a card’s Folder menu, to create a folder')}
                onDragOver={draggingItem ? e => e.preventDefault() : undefined}
                onDrop={draggingItem ? e => { e.preventDefault(); dropToNewFolder(); } : undefined}
                className={`w-full flex items-center gap-2 px-3 py-1.5 text-left text-[12px] border-t border-zinc-800/70 transition-colors ${
                  draggingItem ? 'text-violet-300 bg-violet-500/10 ring-1 ring-inset ring-violet-400/40' : 'text-zinc-500 hover:text-violet-400 hover:bg-zinc-800/60'
                }`}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-3.5 h-3.5"><path strokeLinecap="round" d="M12 5v14M5 12h14" /></svg>
                New folder
              </button>
            </div>
          )}

          {GROUPS.map(group => {
            const rows = (Object.keys(TYPE_META) as LibraryType[]).filter(t => TYPE_META[t].group === group);
            return (
              <div key={group} className="border border-zinc-800/70 rounded-xl overflow-hidden bg-zinc-900">
                <h3 className="px-3 pt-2 pb-1.5 text-[10.5px] tracking-[0.09em] uppercase text-zinc-500 font-bold">{group}</h3>
                {rows.map(t => {
                  const n = counts[t] ?? 0;
                  return (
                    <NavRow
                      key={t} label={TYPE_META[t].label} count={n} swatch={TYPE_META[t].hex}
                      active={cat === t} dim={n === 0}
                      onClick={() => pickFilter(() => setCat(cat === t ? 'all' : t))}
                    />
                  );
                })}
              </div>
            );
          })}
          </div>
        </nav>

        {/* ── main ── */}
        <div className="min-w-0">
          {/* toolbar */}
          <div className="flex flex-wrap gap-2.5 items-center mb-4">
            <label className="relative flex-1 min-w-[200px]">
              <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
              <input
                value={q} onChange={e => setQ(e.target.value)} placeholder="Search saved builds…"
                className="w-full bg-zinc-900 border border-zinc-800 rounded-lg pl-8 pr-3 py-2 text-[13px] text-zinc-200 placeholder-zinc-600 focus:outline-none focus:border-violet-500/50 focus:ring-1 focus:ring-violet-500/30"
              />
            </label>
            <select value={ver} onChange={e => setVer(e.target.value)} className="bg-zinc-900 border border-zinc-800 rounded-lg text-[12.5px] text-zinc-200 py-2 pl-3 pr-8 cursor-pointer focus:outline-none focus:border-violet-500/50 appearance-none" style={selectBg}>
              <option value="">All versions</option>
              {versions.map(v => <option key={v} value={v}>{v}</option>)}
            </select>
            <select value={sort} onChange={e => setSort(e.target.value as typeof sort)} className="bg-zinc-900 border border-zinc-800 rounded-lg text-[12.5px] text-zinc-200 py-2 pl-3 pr-8 cursor-pointer focus:outline-none focus:border-violet-500/50 appearance-none" style={selectBg}>
              <option value="new">Newest first</option>
              <option value="old">Oldest first</option>
              <option value="az">Name A–Z</option>
              <option value="type">By type</option>
            </select>
            <div className="flex border border-zinc-800 rounded-lg overflow-hidden">
              <ViewBtn active={view === 'grid'} onClick={() => setView('grid')} label="Grid">
                <svg viewBox="0 0 24 24" fill="currentColor" className="w-3.5 h-3.5"><rect x="3" y="3" width="7" height="7" rx="1.5" /><rect x="14" y="3" width="7" height="7" rx="1.5" /><rect x="3" y="14" width="7" height="7" rx="1.5" /><rect x="14" y="14" width="7" height="7" rx="1.5" /></svg>
              </ViewBtn>
              <ViewBtn active={view === 'list'} onClick={() => setView('list')} label="List">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-3.5 h-3.5"><path strokeLinecap="round" d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01" /></svg>
              </ViewBtn>
            </div>
            <button
              onClick={() => (selecting ? exitSelect() : setSelecting(true))}
              className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-2 text-[12.5px] transition-colors ${selecting ? 'bg-emerald-500/10 border-emerald-500/50 text-emerald-400' : 'bg-zinc-900 border-zinc-800 text-zinc-400 hover:text-zinc-200 hover:border-zinc-700'}`}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-3.5 h-3.5"><path strokeLinecap="round" strokeLinejoin="round" d="M9 11l3 3L22 4M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11" /></svg>
              {selecting ? 'Done' : 'Select'}
            </button>
            <button
              onClick={() => exportItems(visible)}
              title="Download the currently shown items as a JSON backup"
              className="inline-flex items-center gap-1.5 rounded-lg border border-zinc-800 bg-zinc-900 px-3 py-2 text-[12.5px] text-zinc-400 hover:text-zinc-200 hover:border-zinc-700"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-3.5 h-3.5"><path strokeLinecap="round" strokeLinejoin="round" d="M12 3v12m0 0l-4-4m4 4l4-4M4 17v2a2 2 0 002 2h12a2 2 0 002-2v-2" /></svg>
              Export
            </button>
            {showSourceFilter && (
              <div className="flex border border-zinc-800 rounded-lg overflow-hidden">
                {(['all', 'vanilla', 'modded'] as const).map(s => (
                  <button
                    key={s} onClick={() => setSource(s)}
                    className={`px-3 py-2 text-[12px] font-medium capitalize transition-colors ${source === s ? 'bg-zinc-800 text-zinc-100' : 'bg-zinc-900 text-zinc-500 hover:text-zinc-300'}`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* tag filter bar */}
          {(scopedTags.length > 0 || tags.size > 0) && (
            <div className="flex items-center gap-2 flex-wrap mb-3.5">
              <span className="text-[11px] tracking-wide uppercase text-zinc-600 font-semibold mr-0.5">Tags</span>
              {scopedTags.map(({ t, n }) => (
                <button
                  key={t} onClick={() => (tags.has(t) ? removeTag(t) : addTag(t))}
                  className={`text-[11.5px] px-2.5 py-1 rounded-full border transition-colors inline-flex items-center gap-1.5 ${
                    tags.has(t) ? 'bg-emerald-500/10 border-emerald-500/50 text-emerald-400' : 'bg-zinc-900 border-zinc-800 text-zinc-400 hover:text-zinc-200 hover:border-zinc-700'
                  }`}
                >
                  {t}<span className="text-[10px] opacity-70 tabular-nums">{n}</span>
                </button>
              ))}
              {[...tags].filter(t => !scopedTags.some(s => s.t === t)).map(t => (
                <button key={t} onClick={() => removeTag(t)} className="text-[11.5px] px-2.5 py-1 rounded-full border bg-emerald-500/10 border-emerald-500/50 text-emerald-400">{t}</button>
              ))}
              {tags.size > 0 && <button onClick={() => setTags(new Set())} className="text-[11px] text-zinc-500 underline px-1">clear tags</button>}
            </div>
          )}

          {/* result line */}
          <div className="flex items-center justify-between mb-3 px-0.5 text-[12px] text-zinc-500">
            <Breadcrumb cat={cat} folder={folder} tags={tags} />
            <span>{visible.length} {visible.length === 1 ? 'item' : 'items'}{query || ver ? ' (filtered)' : ''}</span>
          </div>

          {/* gallery */}
          {loading ? (
            <div className="flex justify-center py-16"><div className="w-6 h-6 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" /></div>
          ) : visible.length === 0 ? (
            <EmptyState hasData={items.length > 0} onClear={clearFilters} />
          ) : (
            <div className={view === 'grid' ? 'grid gap-3.5 [grid-template-columns:repeat(auto-fill,minmax(210px,1fr))]' : 'flex flex-col gap-2'}>
              {visible.slice(0, shownCount).map(it => (
                <LibraryCard
                  key={it.uid} item={it} view={view}
                  showFolder={cat === 'all' && folder !== it.folder}
                  copied={copiedUid === it.uid}
                  onCopyShare={copyShare}
                  onDelete={remove}
                  onTagClick={addTag}
                  onOpenTool={openTool}
                  onOpenModal={it2 => setModalUid(it2.uid)}
                  onDragStartItem={setDraggingItem}
                  onDragEndItem={() => setDraggingItem(null)}
                  dragging={draggingItem?.uid === it.uid}
                  selecting={selecting}
                  selected={selected.has(it.uid)}
                  onToggleSelect={toggleSelect}
                />
              ))}
            </div>
          )}
          {!loading && visible.length > shownCount && (
            <div className="flex justify-center mt-5">
              <button
                onClick={() => setShown({ key: filterKey, n: shownCount + PAGE_SIZE })}
                className={`px-4 py-2 text-sm rounded-lg ${SECONDARY_BUTTON}`}
              >
                Show {Math.min(PAGE_SIZE, visible.length - shownCount).toLocaleString('en-US')} more
                <span className="text-zinc-500"> · {(visible.length - shownCount).toLocaleString('en-US')} not shown</span>
              </button>
            </div>
          )}

          {selecting && selected.size > 0 && (
            <div className="sticky bottom-[calc(1rem+var(--cookie-banner-h,0px)+var(--ad-anchor-bottom,0px))] mt-4 z-20 flex items-center gap-2.5 flex-wrap px-3.5 py-2.5 rounded-xl border border-emerald-500/40 bg-gradient-to-b from-[#152019] to-[#101613] shadow-2xl">
              <span className="text-[13px] font-semibold text-emerald-400">{selected.size} selected</span>
              <span className="flex-1" />
              <BulkBtn onClick={bulkMove} label="Move to folder"><path strokeLinecap="round" strokeLinejoin="round" d="M3 7a2 2 0 012-2h4l2 2h8a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z" /></BulkBtn>
              <BulkBtn onClick={bulkTag} label="Tag"><path strokeLinecap="round" strokeLinejoin="round" d="M7 7h.01M3 5v6.6a2 2 0 00.6 1.4l7.4 7.4a2 2 0 002.8 0l5.2-5.2a2 2 0 000-2.8L13.6 5.6A2 2 0 0012.2 5H5a2 2 0 00-2 2z" /></BulkBtn>
              <BulkBtn onClick={() => { exportItems(selectedItems); }} label="Export"><path strokeLinecap="round" strokeLinejoin="round" d="M12 3v12m0 0l-4-4m4 4l4-4M4 17v2a2 2 0 002 2h12a2 2 0 002-2v-2" /></BulkBtn>
              <BulkBtn onClick={bulkDelete} label="Delete" danger><path strokeLinecap="round" d="M4 7h16M9 7V5a1 1 0 011-1h4a1 1 0 011 1v2m-9 0l1 12a2 2 0 002 2h6a2 2 0 002-2l1-12" /></BulkBtn>
            </div>
          )}
        </div>
      </div>
      )}

      {modalItem && (
        <QuickLookModal
          key={modalItem.uid}
          item={modalItem}
          folders={folders}
          allTags={allTags}
          copied={copiedUid === modalItem.uid}
          onClose={() => setModalUid(null)}
          onRename={handleRename}
          onSetFolder={moveToFolder}
          onSetTags={setItemTags}
          onSetPinned={setItemPinned}
          onDuplicate={(item) => {
            // The hook returns { error }. Passing `duplicate` straight through dropped it, so a copy
            // that lost the unique-index race flashed in and vanished with no message at all.
            void duplicate(item).then(({ error }) => { if (error) showToast(error); });
          }}
          onDelete={remove}
          onCopyShare={copyShare}
          onOpenTool={openTool}
          onToast={showToast}
        />
      )}

      {toast && (
        <div className="fixed bottom-[calc(1.5rem+var(--cookie-banner-h,0px)+var(--ad-anchor-bottom,0px))] left-1/2 -translate-x-1/2 z-[80] bg-zinc-800 border border-emerald-500/40 text-zinc-100 rounded-lg px-4 py-2.5 text-[13px] shadow-xl">
          {toast}
        </div>
      )}
    </div>
  );
}

const selectBg: React.CSSProperties = {
  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' fill='%236b746c' viewBox='0 0 16 16'%3E%3Cpath d='M8 11L3 6h10z'/%3E%3C/svg%3E")`,
  backgroundRepeat: 'no-repeat', backgroundPosition: 'right 10px center',
};

function NavRow({ label, count, active, onClick, swatch, folder, italic, dim, dropActive, onItemDrop }: {
  label: string; count: number; active: boolean; onClick: () => void;
  swatch?: string; folder?: boolean; italic?: boolean; dim?: boolean;
  dropActive?: boolean; onItemDrop?: () => void;
}) {
  const [over, setOver] = useState(false);
  const canDrop = dropActive && !!onItemDrop;
  return (
    <button
      onClick={onClick}
      onDragOver={canDrop ? e => { e.preventDefault(); setOver(true); } : undefined}
      onDragLeave={canDrop ? () => setOver(false) : undefined}
      onDrop={canDrop ? e => { e.preventDefault(); setOver(false); onItemDrop!(); } : undefined}
      className={`w-full flex items-center gap-2.5 px-3 py-1.5 text-left text-[13px] transition-colors ${
        active ? (folder ? 'bg-violet-500/10 text-zinc-100' : 'bg-emerald-500/10 text-zinc-100') : 'text-zinc-400 hover:bg-zinc-800/60 hover:text-zinc-200'
      } ${dim ? 'opacity-70' : ''} ${over ? 'ring-1 ring-inset ring-violet-400/70 bg-violet-500/10' : ''}`}
    >
      {folder ? (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className={`w-3.5 h-3.5 shrink-0 ${active ? 'text-violet-400' : 'text-zinc-500'}`}><path strokeLinecap="round" strokeLinejoin="round" d="M3 7a2 2 0 012-2h4l2 2h8a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z" /></svg>
      ) : (
        <span className="w-2 h-2 rounded-sm shrink-0" style={{ background: swatch }} />
      )}
      <span className={`flex-1 min-w-0 truncate ${italic ? 'italic' : ''}`}>{label}</span>
      <span className={`text-[11px] tabular-nums ${active ? (folder ? 'text-violet-400' : 'text-emerald-400') : 'text-zinc-400'}`}>{count}</span>
    </button>
  );
}

// Folder rows are drop targets AND carry hover rename/delete controls, so they can't reuse NavRow.
function FolderRow({ name, count, active, dropActive, onClick, onDrop, onRename, onDelete }: {
  name: string; count: number; active: boolean; dropActive: boolean;
  onClick: () => void; onDrop: () => void; onRename: () => void; onDelete: () => void;
}) {
  const [over, setOver] = useState(false);
  return (
    <div
      onDragOver={dropActive ? e => { e.preventDefault(); setOver(true); } : undefined}
      onDragLeave={dropActive ? () => setOver(false) : undefined}
      onDrop={dropActive ? e => { e.preventDefault(); setOver(false); onDrop(); } : undefined}
      className={`group/fr relative flex items-center gap-2.5 px-3 py-1.5 text-[13px] transition-colors ${
        active ? 'bg-violet-500/10 text-zinc-100' : 'text-zinc-400 hover:bg-zinc-800/60 hover:text-zinc-200'
      } ${over ? 'ring-1 ring-inset ring-violet-400/70 bg-violet-500/10' : ''}`}
    >
      <button onClick={onClick} className="flex items-center gap-2.5 flex-1 min-w-0 text-left">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className={`w-3.5 h-3.5 shrink-0 ${active ? 'text-violet-400' : 'text-zinc-500'}`}><path strokeLinecap="round" strokeLinejoin="round" d="M3 7a2 2 0 012-2h4l2 2h8a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z" /></svg>
        <span className="flex-1 min-w-0 truncate">{name}</span>
      </button>
      {/* ⚠️ These two buttons are the ONLY way to rename or delete a folder, so they must never be
          `display:none`. `hidden group-hover:flex` removed them from the tab order entirely, which
          made both operations unreachable by keyboard AND by every touch user — there is no hover
          on a phone, so the folder list was append-only there. `opacity-0` keeps them focusable,
          and focus-within reveals them the moment they are tabbed to (the pattern LibraryCard
          already uses). They are absolutely positioned so revealing them does not reflow the row. */}
      {/* ⚠️ The `hover:none` variants are what make this work on iPhone and iPad. Tailwind 4 wraps
          `group-hover` in `@media (hover: hover)`, so on touch devices only focus-within could
          reveal these — and iOS Safari does not focus a <button> on tap. The span stayed invisible
          and pointer-events-none there, leaving folders append-only on iOS, which is the defect
          this row was meant to fix. On a no-hover device the buttons are simply always shown, in
          normal flow so they do not overlap the count. */}
      <span className="absolute right-2 flex items-center gap-0.5 opacity-0 pointer-events-none transition-opacity group-hover/fr:opacity-100 group-hover/fr:pointer-events-auto group-focus-within/fr:opacity-100 group-focus-within/fr:pointer-events-auto [@media(hover:none)]:static [@media(hover:none)]:opacity-100 [@media(hover:none)]:pointer-events-auto">
        <button onClick={onRename} aria-label={`Rename folder ${name}`} title="Rename folder" className="text-zinc-500 hover:text-zinc-200 p-0.5"><svg aria-hidden viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-3 h-3"><path strokeLinecap="round" strokeLinejoin="round" d="M4 20h4l10-10-4-4L4 16zM13 5l4 4" /></svg></button>
        <button onClick={onDelete} aria-label={`Delete folder ${name}`} title="Delete folder" className="text-zinc-500 hover:text-red-400 p-0.5"><svg aria-hidden viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-3 h-3"><path strokeLinecap="round" d="M4 7h16M9 7V5h6v2m-8 0l1 12h8l1-12" /></svg></button>
      </span>
      {/* `pointer-events-none` is load-bearing, not tidiness. This used to be `group-hover:hidden`,
          which removed it from the layer entirely; fading it with `opacity-0` leaves it hit-testable
          and sitting directly over the absolutely-positioned buttons above, so it swallowed every
          click on Rename/Delete. It is decorative text and should never take a pointer event. */}
      <span className={`text-[11px] tabular-nums pointer-events-none group-hover/fr:opacity-0 group-focus-within/fr:opacity-0 ${active ? 'text-violet-400' : 'text-zinc-400'}`}>{count}</span>
    </div>
  );
}

function ViewBtn({ active, onClick, label, children }: { active: boolean; onClick: () => void; label: string; children: React.ReactNode }) {
  return (
    <button onClick={onClick} title={label} aria-label={label} className={`px-2.5 py-2 flex ${active ? 'bg-zinc-800 text-zinc-100' : 'bg-zinc-900 text-zinc-500 hover:text-zinc-300'}`}>{children}</button>
  );
}

function BulkBtn({ onClick, label, danger, children }: { onClick: () => void; label: string; danger?: boolean; children: React.ReactNode }) {
  return (
    <button onClick={onClick} className={`inline-flex items-center gap-1.5 rounded-lg border border-zinc-800 bg-zinc-800/60 px-2.5 py-1.5 text-[12px] transition-colors ${danger ? 'text-zinc-300 hover:text-red-400 hover:border-red-500/40' : 'text-zinc-300 hover:text-zinc-100'}`}>
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-3.5 h-3.5">{children}</svg>{label}
    </button>
  );
}

function Breadcrumb({ cat, folder, tags }: { cat: CatFilter; folder: string | null; tags: Set<string> }) {
  const parts: React.ReactNode[] = [];
  if (folder) parts.push(<b key="f" className="text-zinc-200">{folder === UNFILED ? 'Unfiled' : folder}</b>);
  if (cat !== 'all') parts.push(<b key="c" className="text-zinc-200">{TYPE_META[cat as LibraryType].label}</b>);
  [...tags].forEach(t => parts.push(<span key={`t-${t}`} className="text-emerald-400">#{t}</span>));
  if (parts.length === 0) return <span>Showing <b className="text-zinc-200">all builds</b></span>;
  return (
    <span className="flex items-center gap-1.5 flex-wrap">
      Showing {parts.map((p, i) => <span key={i} className="flex items-center gap-1.5">{i > 0 && <span className="text-zinc-700">▸</span>}{p}</span>)}
    </span>
  );
}

function EmptyState({ hasData, onClear }: { hasData: boolean; onClear: () => void }) {
  return (
    <div className="bg-zinc-900/50 border border-zinc-800 border-dashed rounded-xl p-10 text-center">
      <svg className="w-12 h-12 mx-auto text-zinc-700 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" /></svg>
      {hasData ? (
        <>
          <p className="text-sm font-medium text-zinc-400 mb-1">No results.</p>
          <p className="text-xs text-zinc-500">Nothing matches these filters. <button onClick={onClear} className="text-emerald-400 hover:underline">Clear filters</button>.</p>
        </>
      ) : (
        <>
          <p className="text-sm font-medium text-zinc-400 mb-1">Nothing saved yet.</p>
          <p className="text-xs text-zinc-500">Star items on the <Link href="/tools/id-list" className="text-emerald-400 hover:underline">ID List</Link>, or save a build from any editor — it all lands here.</p>
        </>
      )}
    </div>
  );
}
